import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Save, WifiOff } from 'lucide-react';
import { User } from '../types';
import { fetchBookings, Booking, isLifecycleConnected } from '../utils/lifecycleApi';

const STORAGE_KEY = 'ss_lesson_learned_v1';
function load(): Record<number, any> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
}
function save(d: any) { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); }

const PROMPTS = {
  went_well: [
    'Customer communication was smooth',
    'Team arrived on time and was well-prepared',
    'Shot list was followed properly',
    'Data backup was done immediately after event',
    'Customer was happy with the output',
  ],
  went_wrong: [
    'Bride/Groom was not ready on time — missed shots',
    'Equipment issue on event day',
    'Contractor did not follow briefing',
    'Data submission was delayed',
    'Customer asked for corrections beyond agreed limit',
    'Standee/banner was not handled properly',
    'Mic/sound coordination with event planner was missing',
    'Timeline tracking was not done during event',
    'WhatsApp group had too many members',
  ],
  action_next: [
    'Update T&C: readiness responsibility on customer',
    'Update SOP: escalation channel must be followed',
    'Brief contractor more specifically before next event',
    'Prepare equipment checklist earlier',
    'Add correction limit to next customer communication',
    'Raw data policy to be communicated at booking stage',
  ],
};

export default function LessonLearned({ user }: { user: User }) {
  const [connected] = useState(isLifecycleConnected());
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Booking | null>(null);
  const [data, setData] = useState(load());
  const [saved, setSaved] = useState(false);

  const load2 = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try { setBookings(await fetchBookings()); } catch {} finally { setLoading(false); }
  }, [connected]);

  useEffect(() => { load2(); }, [load2]);

  const get = (bookingId: number) => data[bookingId] || { went_well: '', went_wrong: '', action_next: '', sop_update: 'No', rate_update: 'No', tc_update: 'No', overall_rating: '3', additional: '' };
  const update = (bookingId: number, field: string, value: string) => {
    const updated = { ...data, [bookingId]: { ...get(bookingId), [field]: value } };
    setData(updated); save(updated); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const addPrompt = (bookingId: number, field: 'went_well'|'went_wrong'|'action_next', text: string) => {
    const current = get(bookingId)[field];
    const newVal = current ? `${current}\n• ${text}` : `• ${text}`;
    update(bookingId, field, newVal);
  };

  if (!connected) return (
    <div className="page-content">
      <div className="alert alert-error"><WifiOff size={15} /> Not connected to Lifecycle backend.</div>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Lesson Learned</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:13 }}>Capture what worked, what didn't, and what to improve. Every event makes the next one better.</p>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={load2}><RefreshCw size={15} /></button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:20 }}>
        <div>
          <label style={{ fontSize:12, marginBottom:8, display:'block' }}>Select Booking</label>
          {loading ? <div style={{ color:'var(--text-muted)', fontSize:13 }}>Loading…</div> : bookings.map(b => {
            const d = data[b.id];
            return (
              <div key={b.id} onClick={() => setSelected(b)}
                style={{ padding:'10px 14px', borderRadius:8, marginBottom:6, cursor:'pointer',
                  border:`1.5px solid ${selected?.id===b.id?'var(--teal)':'var(--border)'}`,
                  background:selected?.id===b.id?'rgba(56,178,172,0.06)':'#fff' }}>
                <div style={{ fontWeight:600, fontSize:13 }}>{b.customer_name}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)' }}>{b.csr_number} · {b.service_type}</div>
                {d && <div style={{ fontSize:10, color:'var(--teal)', marginTop:2 }}>✓ Lesson Learned recorded</div>}
              </div>
            );
          })}
        </div>

        <div>
          {!selected ? (
            <div className="card" style={{ textAlign:'center', padding:60, color:'var(--text-muted)' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>📝</div>
              <div style={{ fontWeight:600 }}>Select a booking to record lessons</div>
              <div style={{ fontSize:13, marginTop:4 }}>Best done after Post-Event stage is complete</div>
            </div>
          ) : (() => {
            const d = get(selected.id);
            return (
              <div>
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:15 }}>{selected.customer_name} — {selected.csr_number}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>{selected.service_type} · Stage: {selected.current_stage}</div>
                  {saved && <div style={{ fontSize:12, color:'var(--teal)', marginTop:6 }}>✓ Saved automatically</div>}
                </div>

                {/* Rating */}
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'var(--gold)', marginBottom:12 }}>Overall Event Rating</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {['1','2','3','4','5'].map(r => (
                      <button key={r} onClick={() => update(selected.id,'overall_rating',r)}
                        style={{ width:40, height:40, borderRadius:8, border:`2px solid ${d.overall_rating===r?'var(--gold)':'var(--border)'}`,
                          background:d.overall_rating===r?'#FFFBEB':'#fff', cursor:'pointer', fontSize:18, fontFamily:'inherit' }}>
                        {['😟','😕','😐','😊','🌟'][Number(r)-1]}
                      </button>
                    ))}
                    <span style={{ fontSize:12, color:'var(--text-muted)', alignSelf:'center', marginLeft:8 }}>
                      {['Very Poor','Poor','Average','Good','Excellent'][Number(d.overall_rating)-1]}
                    </span>
                  </div>
                </div>

                {/* What went well */}
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#059669', marginBottom:8 }}>✅ What Went Well</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    {PROMPTS.went_well.map(p => (
                      <button key={p} onClick={() => addPrompt(selected.id,'went_well',p)}
                        style={{ fontSize:11, padding:'3px 10px', borderRadius:100, cursor:'pointer', fontFamily:'inherit',
                          background:'#F0FDF4', color:'#065F46', border:'1px solid #86EFAC' }}>+ {p}</button>
                    ))}
                  </div>
                  <textarea className="textarea" rows={4} placeholder="What worked well? What should we repeat next time?"
                    value={d.went_well} onChange={e => update(selected.id,'went_well',e.target.value)} />
                </div>

                {/* What went wrong */}
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#DC2626', marginBottom:8 }}>❌ What Went Wrong / Could Be Better</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    {PROMPTS.went_wrong.map(p => (
                      <button key={p} onClick={() => addPrompt(selected.id,'went_wrong',p)}
                        style={{ fontSize:11, padding:'3px 10px', borderRadius:100, cursor:'pointer', fontFamily:'inherit',
                          background:'#FEF2F2', color:'#7F1D1D', border:'1px solid #FECACA' }}>+ {p}</button>
                    ))}
                  </div>
                  <textarea className="textarea" rows={4} placeholder="What issues came up? What needs to be fixed?"
                    value={d.went_wrong} onChange={e => update(selected.id,'went_wrong',e.target.value)} />
                </div>

                {/* Action for next time */}
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#1D4ED8', marginBottom:8 }}>🔧 Action for Next Time</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                    {PROMPTS.action_next.map(p => (
                      <button key={p} onClick={() => addPrompt(selected.id,'action_next',p)}
                        style={{ fontSize:11, padding:'3px 10px', borderRadius:100, cursor:'pointer', fontFamily:'inherit',
                          background:'#EFF6FF', color:'#1E3A5F', border:'1px solid #BFDBFE' }}>+ {p}</button>
                    ))}
                  </div>
                  <textarea className="textarea" rows={4} placeholder="What specific action will be taken? Who is responsible?"
                    value={d.action_next} onChange={e => update(selected.id,'action_next',e.target.value)} />
                </div>

                {/* Update flags */}
                <div className="card" style={{ marginBottom:14 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'var(--gold)', marginBottom:12 }}>Update Required?</div>
                  <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
                    {[['sop_update','SOP Update'],['tc_update','T&C Update'],['rate_update','Rate Update']].map(([field,label]) => (
                      <div key={field}>
                        <div style={{ fontSize:12, marginBottom:6, fontWeight:600 }}>{label}</div>
                        <div style={{ display:'flex', gap:6 }}>
                          {['Yes','No','Maybe'].map(v => (
                            <button key={v} onClick={() => update(selected.id,field,v)}
                              style={{ padding:'4px 12px', borderRadius:100, fontSize:12, cursor:'pointer', fontFamily:'inherit',
                                background:(d as any)[field]===v?(v==='Yes'?'#FEE2E2':v==='No'?'#F0FDF4':'#FFFBEB'):'#F3F4F6',
                                color:(d as any)[field]===v?(v==='Yes'?'#7F1D1D':v==='No'?'#065F46':'#92400E'):'#6B7280',
                                border:`1px solid ${(d as any)[field]===v?'currentColor':'transparent'}`,fontWeight:(d as any)[field]===v?700:400 }}>
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional notes */}
                <div className="card">
                  <div style={{ fontWeight:700, fontSize:13, color:'var(--gold)', marginBottom:8 }}>Additional Notes</div>
                  <textarea className="textarea" rows={3} placeholder="Any other observations, customer feedback, team feedback..."
                    value={d.additional} onChange={e => update(selected.id,'additional',e.target.value)} />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
